import { useCallback } from 'react';
import { useQuery } from 'react-query';

import {
  asStreakError,
  useChooseStreakGoalMutation,
  useClaimStreakGoalMutation,
  useStreakSummary,
} from 'src/queries/streak';
import type { StreakGoalRead } from 'src/types/streak.types';
import { User } from 'src/types/user.types';
import { queryKeys } from 'src/queries/keys';

export type StreakGoal = {
  targetDays: number;
  rewardBabz: number;

  baselineStreak: number;
  chosenAt: string;
  claimedAt: string | null;

  status?: StreakGoalRead['status'];
};

export const toStreakGoal = (read: StreakGoalRead): StreakGoal => ({
  targetDays: read.target_days,
  rewardBabz: read.reward_babz,
  baselineStreak: read.baseline_length,
  chosenAt: read.chosen_on,
  claimedAt: read.claimed_at,
  status: read.status,
});

export const scopedStorageKey = (base: string, userId?: number) =>
  userId === undefined ? base : `${base}:${userId}`;

export const streakGoalProgress = (goal: StreakGoal, streakLength: number) =>
  Math.min(Math.max(streakLength - goal.baselineStreak, 0), goal.targetDays);

export const isStreakGoalReached = (goal: StreakGoal, streakLength: number) =>
  streakGoalProgress(goal, streakLength) >= goal.targetDays;

export const isStreakGoalActive = (
  goal: StreakGoal | null,
  streakLength: number,
): goal is StreakGoal =>
  goal !== null && !goal.claimedAt && streakLength >= goal.baselineStreak;

export const useCurrentUserId = (): number | undefined => {
  const { data } = useQuery<User>(queryKeys.user(), { enabled: false });
  return data?.id;
};

export const useStreakGoal = ({ isEnabled = true } = {}): StreakGoal | null => {
  const { data } = useStreakSummary({ isEnabled });
  return data?.goal ? toStreakGoal(data.goal) : null;
};

export const useStreakGoalOptions = () => {
  const { data } = useStreakSummary();
  return (data?.goal_options ?? []).map(option => ({
    targetDays: option.target_days,
    rewardBabz: option.reward_babz,
  }));
};

export const useStreakCelebrationTiers = (
  { isEnabled = true } = {},
): number[] => {
  const { data } = useStreakSummary({ isEnabled });
  return data?.celebration_tiers ?? [];
};

export const useChooseStreakGoal = () => {
  const { mutate, isLoading, isError, error } = useChooseStreakGoalMutation();
  const chooseGoal = useCallback(
    (targetDays: number, onCommitted: () => void) => {
      mutate({ target_days: targetDays }, { onSuccess: () => onCommitted() });
    },
    [mutate],
  );

  return {
    chooseGoal,
    isCommitting: isLoading,
    hasFailed: isError,
    errorCode: asStreakError(error),
  };
};

export const useClaimStreakGoal = () => {
  const { mutate } = useClaimStreakGoalMutation();
  return useCallback(() => {
    mutate();
  }, [mutate]);
};
